import { CustomButton } from "@/components/custom/custom-button";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { showToast } from "@/redux/actions/toast-actions";
import { newCommissionPayoutSchema } from "@/schemas/pricing-config.schema";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  styled,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useProductCategories } from "@/hooks/use-products-categories";
import { useAllCommission } from "@/hooks/use-all-commission";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 500,
    width: "100%",
  },
}));

const transformPayload = (values) => ({
  categoryId: values.categoryId,
  commissionId: values.commissionId,
  percentage: Number(values.percentage),
});

const CommissionPayoutDialog = ({ open, onClose, mutationFn, payout }) => {
  const isEditing = Boolean(payout);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: categoriesData } = useProductCategories(true);
  const { data: commissionsData } = useAllCommission(true, {
    page: 0,
    size: 100,
  });

  const categoryOptions = (categoriesData?.data?.data ?? []).map((cat) => ({
    label: cat.name,
    value: cat.id,
  }));

  const commissionOptions = (commissionsData?.data?.data ?? []).map((com) => ({
    label: com.name,
    value: com.id,
  }));

  const formik = useFormik({
    initialValues: {
      categoryId: payout?.category?.id ?? "",
      commissionId: payout?.commission?.id ?? "",
      percentage: payout?.percentage ?? "",
    },
    enableReinitialize: true,
    validationSchema: newCommissionPayoutSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate(transformPayload(values));
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) => mutationFn(payload),
    onSuccess: (data) => {
      formik.resetForm();
      dispatch(
        showToast(
          data?.message ?? (isEditing ? "Payout updated" : "Payout created"),
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["all-commission-payouts"] });
      onClose();
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Something went wrong", "error"));
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {isEditing
              ? "Update Commission Payout"
              : "Create Commission Payout"}
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {isEditing
              ? "Edit the details below to update this payout configuration."
              : "Fill in the details below to create a new payout configuration."}
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormSelectInput
            options={categoryOptions}
            formik={formik}
            name="categoryId"
            label="Category"
          />
          <FormSelectInput
            options={commissionOptions}
            formik={formik}
            name="commissionId"
            label="Commission"
          />
          <FormTextInput
            formik={formik}
            name="percentage"
            label="Percentage (0 - 100)"
            type="number"
          />
        </Stack>

        {mutation.isError && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {mutation.error?.data?.[0] ?? "Failed. Try again."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 2, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending
            ? isEditing
              ? "Updating..."
              : "Creating..."
            : isEditing
              ? "Update Payout"
              : "Create Payout"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default CommissionPayoutDialog;
